import React from "react";
import { Link, useLocation } from "react-router-dom";

type Page = {
  title: string;
  intro: string;
  sections: Array<[string, React.ReactNode]>;
};

const pages: Record<string, Page> = {
  "/about": {
    title: "About Observer Theory",
    intro:
      "The public accountability record for manifestation and law of attraction coaches, law of assumption teachers, tarot and oracle readers, psychics, mediums, astrologers, energy practitioners, spiritual teachers, and related transformational practices.",
    sections: [
      [
        "Why this world needs a record",
        "Spiritual and transformational work often lives across YouTube channels, social profiles, books, courses, readings, communities, and personal websites. Observer Theory brings that scattered public record together without reducing a person to marketing copy or anonymous commentary.",
      ],
      [
        "Category-aware, belief-neutral",
        "We document what someone publicly teaches—whether they call it manifestation, conscious creation, law of attraction, law of assumption, divination, mediumship, astrology, energy work, mysticism, or another tradition. We do not decide whether a spiritual claim is true or promise that an outcome is possible.",
      ],
      [
        "Why we exist",
        "An interested party should not control the reputation record. Observer Theory structures dated public facts and evidence-graded firsthand reviews while guaranteeing the subject a free right of reply.",
      ],
      [
        "Launch independence",
        "Observer Theory has no billing, advertising, affiliate, pay-per-lead, or sponsored-placement product at launch. Payment cannot affect ratings, moderation, organic rank, verification, or disputes.",
      ],
    ],
  },
  "/review-policy": {
    title: "Review standard",
    intro:
      "Reviews should help another person understand a real experience with a teaching, reading, course, session, community, coach, or practitioner—not win a campaign.",
    sections: [
      [
        "Firsthand experience",
        "Review only a service, program, reading, course, community, coaching relationship, or body of free public teaching you personally experienced. Do not use AI to write or revise a review. State opinions as opinions and describe facts accurately.",
      ],
      [
        "What reviews can assess",
        "Describe delivery, communication, boundaries, professionalism, value, and your own experience. Observer Theory does not adjudicate whether manifestation works, whether a psychic or tarot reading was spiritually accurate, or whether a belief is true.",
      ],
      [
        "Conflicts and incentives",
        "Self-reviews, employee or family reviews, competitor reviews, undisclosed material relationships, review trading, and incentives conditioned on sentiment are prohibited. A reviewer must disclose a relevant relationship when the form asks for it.",
      ],
      [
        "Privacy and rights",
        "Do not publish private contact information, confidential records, receipt details, copyrighted material you do not have permission to use, threats, harassment, or claims about another person that you cannot support. Evidence belongs in the private evidence channel, not the review body.",
      ],
      [
        "Moderation, replies, and appeals",
        "We moderate for relevance, privacy, threats, harassment, fraud, conflicts, authenticity, intellectual-property complaints, and legal requirements—not because a review is critical. The subject may reply and dispute for free. Both sides receive reasons and may appeal once.",
      ],
      [
        "Removal and withdrawal",
        "A practitioner cannot remove a review merely because it is negative or commercially inconvenient. Reviews may be removed or redacted for a policy breach, reviewer withdrawal, or legal requirement. Reviewers can withdraw their own reviews through support.",
      ],
    ],
  },
  "/privacy": {
    title: "Privacy policy",
    intro:
      "Effective July 21, 2026. Observer Theory, operated by Joseph Alai, collects only what is needed to operate, secure, and improve the accountability record.",
    sections: [
      [
        "Data we process",
        "We process account identifiers, practitioner and public-source information, profile and review submissions, moderation and dispute records, support messages, security logs, device notification tokens, and consented analytics. We may also process public professional information supplied by a practitioner, a nominator, or an identified public source.",
      ],
      [
        "Public and private information",
        "Published profiles, sourced assertions, reviews, and approved replies are public. Email addresses, verification codes, device tokens, fraud signals, moderation notes, purchase evidence, and verification evidence are restricted. Purchase evidence is stored separately, never published, and never shown to the practitioner.",
      ],
      [
        "Why we process it",
        "We use data to operate the record, verify control of profiles and reviewer email addresses, moderate content, prevent fraud, answer disputes and rights requests, send opted-in practitioner notifications, maintain security, and measure the product with consent. Depending on the context, the basis is performing a requested service, consent, legal obligation, or the legitimate interest in operating an accurate and fair public accountability record.",
      ],
      [
        "Analytics and advertising",
        "Analytics is disabled until consent. Advertising identifiers, Google Signals, ad personalization, and seeker identity-linked journey tracking are disabled. We do not attach seeker search terms, profile-subject names, review text, evidence, or dispute content to analytics identities.",
      ],
      [
        "Public-source records",
        "Profile facts come from identified public sources or clearly labelled practitioner statements. Observer Theory does not collect or republish platform comments, comment-author information, follower counts, or audience sentiment, and public-source data is never used to create a hidden quality score.",
      ],
      [
        "Retention and security",
        "We retain information only while needed for the stated purpose, fraud prevention, dispute and audit integrity, or a legal obligation. Private purchase evidence has a one-year maximum operational expiry unless a dispute or legal hold requires shorter or longer retention. Verification secrets expire promptly. We use access controls, private evidence storage, hashing, encryption in transit, and audit logs; no system can guarantee absolute security.",
      ],
      [
        "Your choices and rights",
        "You may request access, correction, objection, restriction, portability, suppression, or deletion where applicable. A practitioner may correct an inaccurate public fact without claiming or paying. Account deletion does not automatically erase accurate public facts, another person's lawful review, or records that must remain for fraud prevention, audit integrity, disputes, or legal obligations.",
      ],
      [
        "Children and contact",
        "Observer Theory is for adults. Minors may not submit reviews or be intentionally listed as profile subjects. Send privacy questions and rights requests to hello@observertheory.com; we may verify identity before acting on a request.",
      ],
    ],
  },
  "/terms": {
    title: "Terms of service",
    intro:
      "Effective July 21, 2026. Observer Theory provides public-record and community-review tools—not guaranteed outcomes.",
    sections: [
      [
        "The service",
        "The record reports and structures sourced public facts, practitioner statements, and attributed reviews. It does not assess spiritual truth, promise outcomes, provide regulated professional advice, or act as the practitioner or reviewer. Profiles may exist without being claimed; claiming verifies control but does not transfer editorial control.",
      ],
      [
        "Eligibility and accounts",
        "You must be at least 18 to submit a review, claim a profile, or provide evidence. Provide accurate account information, protect access credentials, and use only accounts and verification channels you are authorized to control.",
      ],
      [
        "Your content",
        "You retain ownership of content you submit. You grant Observer Theory a worldwide, non-exclusive, royalty-free licence to host, copy, format, display, distribute, moderate, and preserve that content only as needed to operate, secure, explain, and legally defend the service. The licence ends when eligible content is deleted, except for restricted backups, audit records, fraud prevention, disputes, and legal obligations.",
      ],
      [
        "Your promises",
        "You will submit content you have the right to provide; distinguish firsthand experience, opinion, and sourced fact; disclose required conflicts; and avoid fabricated, defamatory, threatening, harassing, privacy-invasive, infringing, promotional, manipulated, or unlawful content. Do not trade reviews or offer compensation conditioned on posting, withholding, changing, or removing a review.",
      ],
      [
        "Public profiles and corrections",
        "Observer Theory may create an unclaimed profile from dated public sources. A profile is not an endorsement, accusation, or statement that a practitioner agrees with the record. Anyone may suggest a factual correction. The subject may reply, dispute, correct, appeal, and report impersonation without paying, upgrading, or claiming the profile.",
      ],
      [
        "Moderation and notice",
        "Observer Theory may screen, label, restrict, redact, remove, reinstate, or preserve content under the published Review Standard, Acceptable Use Policy, intellectual-property requirements, or applicable law. Critical sentiment alone is not a removal ground. Decisions carry reasons and an appeal path. Send a specific content URL, the asserted issue, supporting facts, and your contact information to hello@observertheory.com.",
      ],
      [
        "Evidence and confidentiality",
        "Evidence is used only for verification, moderation, disputes, fraud prevention, and legal obligations. It is not public and is not disclosed to the practitioner. Do not upload full payment-card numbers, unnecessary health information, government identifiers, or unrelated private material.",
      ],
      [
        "Acceptable use and availability",
        "Use the service lawfully and follow the Review Standard and Acceptable Use Policy. Do not impersonate, scrape private areas, compromise security, manipulate search or reviews, or harass another person. Features may change, be suspended, or become unavailable to protect users, comply with law, or maintain the service.",
      ],
      [
        "No guarantee",
        "Public records and user content may be incomplete, disputed, or inaccurate. Evidence labels describe the evidence process, not spiritual truth or guaranteed authenticity. Use independent judgment before purchasing a service or acting on information. Nothing in these terms excludes rights or liability that applicable law does not permit us to exclude.",
      ],
    ],
  },
  "/acceptable-use": {
    title: "Acceptable use",
    intro:
      "Observer Theory is for honest discovery and accountable public conversation.",
    sections: [
      [
        "Prohibited conduct",
        "No fraud, impersonation, fabricated or traded reviews, undisclosed conflicts, harassment, threats, hate, doxxing, sexual exploitation, grooming, financial solicitation, sanctions evasion, intellectual-property infringement, search manipulation, or attempts to compromise the service.",
      ],
      [
        "High-risk claims",
        "Do not market spiritual services as guaranteed medical, legal, financial, fertility, immigration, or crisis outcomes. Predatory dependency, coercion, and pressure to stop professional treatment are prohibited.",
      ],
      [
        "Enforcement",
        "We may investigate, preserve evidence, limit access, remove content, or refer urgent matters when reasonably necessary. Affected users receive a reason and appeal route unless doing so would create a safety, security, or legal risk.",
      ],
    ],
  },
  "/safety": {
    title: "Safety",
    intro:
      "Spiritual and personal-growth services are not substitutes for licensed medical, mental-health, legal, or financial care.",
    sections: [
      [
        "Immediate danger",
        "If you or someone else may be in immediate danger, contact local emergency services. Observer Theory does not provide crisis response.",
      ],
      [
        "Report a concern",
        "Use the report control on a profile or review, or contact hello@observertheory.com. Include links and facts, but do not send unnecessary sensitive information.",
      ],
      [
        "Retaliation and coercion",
        "Report threats, retaliation, extortion, predatory dependency, impersonation, or pressure to abandon professional care through the urgent safety path. These reports receive priority handling and remain separate from public review content.",
      ],
    ],
  },
  "/support": {
    title: "Support",
    intro:
      "Get help with accounts, practitioner records, claims, reviews of readings or services, privacy, factual corrections, and safety reports.",
    sections: [
      [
        "Contact",
        "Email hello@observertheory.com. Transactional replies will come from an Observer Theory address on observertheory.com.",
      ],
      [
        "Practitioner and profile help",
        "We can help manifestation coaches, spiritual teachers, readers, psychics, mediums, astrologers, energy practitioners, and other profile subjects understand submission, claiming, correction, reply, and dispute processes.",
      ],
      [
        "Content and legal notices",
        "Identify the exact profile, assertion, or review; explain whether the request concerns accuracy, privacy, intellectual property, safety, or another legal right; and include supporting information. Do not email unnecessary review evidence or sensitive identifiers.",
      ],
      [
        "Urgent safety",
        "Use the extortion, retaliation, or safety report path for same-day trust-operations triage. Observer Theory is not an emergency service.",
      ],
    ],
  },
  "/account-deletion": {
    title: "Account deletion",
    intro:
      "Request deletion by emailing hello@observertheory.com from your account address.",
    sections: [
      [
        "What happens",
        "We verify the request, revoke sessions, and delete or anonymize eligible personal account data and contributions. Reviewers may withdraw their own reviews.",
      ],
      [
        "What may remain",
        "Restricted fraud-prevention, moderation, evidence, audit, dispute, backup, and legal records may remain only for the applicable retention period or legal obligation. Deleting an account or abandoning a claim does not automatically remove accurate public-source facts or another person's lawful content.",
      ],
    ],
  },
};

const StaticPage: React.FC = () => {
  const { pathname } = useLocation();
  const page = pages[pathname] || {
    title: "Observer Theory",
    intro: "This public page is being prepared.",
    sections: [],
  };
  return (
    <article style={{ maxWidth: 820, margin: "0 auto", padding: "90px 22px" }}>
      <span className="section-kicker">Observer Theory</span>
      <h1
        style={{
          fontFamily: "Manrope,sans-serif",
          fontSize: "clamp(40px,6vw,70px)",
          letterSpacing: "-.065em",
          lineHeight: 1,
          margin: "15px 0 22px",
        }}
      >
        {page.title}
      </h1>
      <p style={{ color: "#66736d", fontSize: 18, lineHeight: 1.7 }}>
        {page.intro}
      </p>
      <div style={{ marginTop: 55 }}>
        {page.sections.map(([title, body]) => (
          <section
            key={title}
            style={{
              padding: "29px 0",
              borderTop: "1px solid rgba(23,35,30,.13)",
            }}
          >
            <h2 style={{ fontFamily: "Manrope,sans-serif", fontSize: 21 }}>
              {title}
            </h2>
            <div style={{ color: "#66736d", lineHeight: 1.8 }}>{body}</div>
          </section>
        ))}
      </div>
      <p style={{ marginTop: 35, fontSize: 12 }}>
        Questions? <Link to="/support">Contact support</Link>.
      </p>
    </article>
  );
};

export default StaticPage;
